/**
 * /founders/new: a founder's brand, slug and subdomain, then where their computer goes (one plan, three regions).
 * The slug and subdomain are checked as you type: their rules, and whether another founder has them. Admins also get
 * every live size for reference. The fields wait on the library's Field (proposed on /__ui) and the region cards on
 * its ChoiceCard, so they still use today's Field and PlanPicker.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { slugify, SLUG_RE, subdomainError, takenBy } from '../../domain/founder'
import { money } from '../../domain/format'
import { newFounderBody } from '../../domain/plans'
import { ApiError } from '../../infra/api'
import { Button, Card, DataTable, EmptyState, ErrorNote, Grid, Inline, Input, Screen, Skeleton, Stack, Text, useNav } from '../../ui'
import { useApi } from '../api-context'
import { PlanPicker, usePlanRegion } from '../components/PlanPicker'
import { Field } from '../components/ui'
import { useLoad } from '../useLoad'

export function NewFounderPage() {
  const { api, me } = useApi()
  const admin = me.person.role === 'admin'
  const { navigate } = useNav()
  const plans = useLoad(() => api.plans(), [api])
  const sizes = useLoad(() => (admin ? api.sizes() : Promise.resolve(null)), [api, admin])
  const founders = useLoad(() => (admin ? api.founders() : Promise.resolve([])), [api, admin])
  const region = usePlanRegion(plans.data ?? null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [subdomain, setSubdomain] = useState('')
  const [subTouched, setSubTouched] = useState(false)
  const [showSizes, setShowSizes] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  /** The API's word on a subdomain it refused, kept until the subdomain changes. */
  const [subRejected, setSubRejected] = useState<{ subdomain: string; reason: string } | null>(null)

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name))
  }, [name, slugTouched])
  useEffect(() => {
    if (!subTouched) setSubdomain(slug)
  }, [slug, subTouched])

  const others = founders.data ?? []
  const slugHolder = takenBy(others, 'slug', slug)
  const subHolder = takenBy(others, 'subdomain', subdomain)
  const slugError = slug && !SLUG_RE.test(slug) ? 'lowercase letters, digits and dashes, starting with a letter' : slugHolder ? `${slugHolder} has this slug` : undefined
  const subError = subdomainError(subdomain, subRejected) ?? (subHolder ? `${subHolder} is on ${subdomain}.cpg.computer` : undefined)
  const input = plans.data ? newFounderBody({ name, slug, subdomain }, plans.data, region.value) : null
  const canSubmit = name.trim() !== '' && SLUG_RE.test(slug) && !slugError && !subError && Boolean(input) && !busy

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit || !input) return
    setBusy(true)
    setError(undefined)
    try {
      const f = await api.createFounder(input)
      navigate(`/founders/${f.id}`)
    } catch (err) {
      if (err instanceof ApiError && err.code === 'bad_subdomain') setSubRejected({ subdomain, reason: err.message })
      else setError(err instanceof ApiError ? err.message : String(err))
      setBusy(false)
    }
  }

  return (
    <Screen title="New founder" crumb={{ label: 'Founders', to: '/founders' }} description="Their brand, the names cpg.computer gives them, and where their computer goes.">
      <form onSubmit={submit}>
        <Stack>
          <Card title="Brand">
            <Grid cols={3}>
              <Field label="Brand name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Field Day" required autoFocus />
              </Field>
              <Field label="Slug" hint="Names the box, the repo and the project. Lowercase, 3 to 32." error={slugError}>
                <Input
                  mono
                  value={slug}
                  onChange={(e) => (setSlugTouched(true), setSlug(e.target.value.toLowerCase()))}
                  placeholder="fieldday"
                  required
                  spellCheck={false}
                  autoCapitalize="off"
                />
              </Field>
              <Field label="Subdomain" hint={subdomain ? <Text mono size="xs">{`${subdomain}.cpg.computer`}</Text> : 'Empty: no address of ours.'} error={subError}>
                <Input mono value={subdomain} onChange={(e) => (setSubTouched(true), setSubdomain(e.target.value.toLowerCase()))} spellCheck={false} autoCapitalize="off" />
              </Field>
            </Grid>
          </Card>

          <Card
            title="Computer"
            action={
              admin ? (
                <Button size="xs" variant="ghost" onClick={() => setShowSizes((v) => !v)} aria-expanded={showSizes}>
                  {showSizes ? 'Hide sizes' : `All sizes${sizes.data ? ` · ${sizes.data.sizes.length} live` : ''}`}
                </Button>
              ) : undefined
            }
          >
            <Stack>
              {plans.error && <ErrorNote title="Plans didn't load." reason={plans.error.message} onRetry={plans.reload} />}
              {!plans.data && !plans.error && <Skeleton height="block" />}
              {plans.data && <PlanPicker plans={plans.data} value={region.value} suggested={region.suggested} onChange={region.setValue} admin={admin} />}
              {admin && showSizes && (
                <Stack gap="sm" as="section" label="All sizes">
                  <Text size="sm" tone="muted">Every live size in US regions, for reference. Founders pick a plan, never a size.</Text>
                  {sizes.error && <ErrorNote title="Sizes didn't load." reason={sizes.error.message} onRetry={sizes.reload} />}
                  {!sizes.data && !sizes.error && <Skeleton height="block" />}
                  {sizes.data && (
                    <DataTable
                      label="All sizes"
                      rows={sizes.data.sizes}
                      rowKey={(s) => s.slug}
                      empty={<EmptyState title="No sizes live.">DigitalOcean listed none in US regions.</EmptyState>}
                      columns={[
                        { key: 'size', label: 'Size', narrow: 'main', render: (s) => <Text weight="head">{`${s.memoryGb} GB · ${s.vcpus} vCPU`}</Text> },
                        { key: 'price', label: 'A month', narrow: 'end', align: 'end', render: (s) => <Text mono size="sm">{money(s.priceMonthly)}</Text> },
                        { key: 'slug', label: 'Slug · disk', render: (s) => <Text mono size="xs" tone="muted">{`${s.slug} · ${s.diskGb} GB disk`}</Text> },
                        { key: 'regions', label: 'Regions', render: (s) => <Text mono size="xs" tone="muted">{s.regions.join(' ')}</Text> },
                      ]}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Card>

          {error && <ErrorNote title="The founder wasn't created." reason={error} />}
          <Inline justify="end">
            <Button variant="ghost" onClick={() => navigate('/founders')}>
              Cancel
            </Button>
            <Button type="submit" variant="ink" disabled={!canSubmit}>
              {busy ? 'Creating…' : 'Create founder'}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Screen>
  )
}
